import { yupResolver } from '@hookform/resolvers/yup';
import { Form, Space } from 'antd';
import { FC, useEffect } from 'react';
import { FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useProgramStepContext } from 'src/features/programsV2/contexts/ProgramStepContext';
import { useCreateOrUpdateProgram } from 'src/features/programsV2/hooks/useCreateOrUpdateProgram';
import {
  ConfigurationStepForm,
  configurationStepSchema,
} from 'src/features/programsV2/schemas/configurationStepSchema';
import { CreateOrUpdateProgramPayload } from 'src/features/programsV2/types/program';
import {
  showErrorModal,
  showSuccessModal,
} from 'src/features/programsV2/utils/modalHelpers';
import { getConfigurationProgramPayload } from 'src/features/programsV2/utils/steps/ConfigurationStep/getConfigurationProgramPayload';

import { ProgramStepFooter } from '../../ProgramStepFooter';
import { ConfigurationForm } from './ConfigurationForm';

interface ConfigurationStepProps {
  programId?: number;
}

export const ConfigurationStep: FC<ConfigurationStepProps> = ({
  programId,
}) => {
  const navigate = useNavigate();

  const { createOrUpdateProgram, isLoading } =
    useCreateOrUpdateProgram(programId);
  const {
    getStepDefaultValues,
    isLoadingProgram,
    isPublishedProgram,
    programApiInitialValues,
    stepValuesRef,
    shouldShowStepErrors,
  } = useProgramStepContext();

  const form = useForm<ConfigurationStepForm>({
    resolver: yupResolver(configurationStepSchema),
    defaultValues:
      getStepDefaultValues('configurationStep') ||
      programApiInitialValues?.configurationStep,
    mode: 'onChange',
  });

  const updatedValues = form.watch();
  const isFormValid = form.formState.isValid;

  const handleUpdateProgram = async (
    payload: Partial<CreateOrUpdateProgramPayload>
  ) => {
    try {
      await createOrUpdateProgram(payload);
      showSuccessModal({
        title: 'Sucesso',
        content: 'Dados do programa atualizados com sucesso!',
      });
    } catch (error) {
      showErrorModal({
        title: 'Tivemos um problema...',
        content:
          'Não foi possível atualizar os dados do programa, tente novamente.',
      });
    }
  };

  const onSubmit = (fields: ConfigurationStepForm) => {
    const payload = getConfigurationProgramPayload(fields);

    programId && handleUpdateProgram(payload);
  };

  const onError: SubmitErrorHandler<ConfigurationStepForm> = errors => {
    const errorField = Object.keys(errors)[0];
    document
      .getElementsByName(errorField)[0]
      .scrollIntoView?.({ block: 'center' });
  };

  useEffect(() => {
    if (stepValuesRef.current && isPublishedProgram)
      stepValuesRef.current['configurationStep'] = {
        isValid: isFormValid,
        values: updatedValues,
        formattedValues: getConfigurationProgramPayload(updatedValues),
      };
  }, [updatedValues, isFormValid, stepValuesRef]);

  useEffect(() => {
    if (shouldShowStepErrors && !isLoadingProgram) form.trigger();
  }, [form, shouldShowStepErrors, isLoadingProgram]);

  return (
    <FormProvider {...form}>
      <Form layout='vertical' onFinish={form.handleSubmit(onSubmit, onError)}>
        <Space direction='vertical' size={24} style={{ width: '100%' }}>
          <ConfigurationForm />
        </Space>
        <ProgramStepFooter
          programId={programId}
          removeNextButton={isPublishedProgram}
          isNextButtonDisabled={!form.formState.isValid}
          onCancelConfirmation={() => navigate('/programs/v2')}
          nextButtonText={programId ? 'Salvar' : 'Próximo'}
          isDisabledCancelButton={isLoading}
          isLoadingNextButton={isLoading}
          style={{
            left: 0,
            right: 0,
            bottom: 0,
            position: 'fixed',
          }}
        />
      </Form>
    </FormProvider>
  );
};
